import { useState, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "antd";
import RevstackDrilldownDrawer from "@components/modals/revstack-modal/RevstackDrilldownDrawer";
import { updateChangeSkuState, updateDrilldownState, updateTrendsState } from "@features/modalSlice";
import RevstackDrilldownTrendsModal from "@/components/modals/revstack-modal/RevstackDrilldownTrendsModal";
import { CA_DD_MODULES, IRevstackPopupData, IRevstackWidgetData, RevstackCASectionIds, RevstackDashboardModules } from "@/ts/common-types";
import { resetDrilldownFilters, resetTrendsFilters } from "@/features/revstackWidgetsSlice";
import RevstackChangeSkuModal from "@/components/modals/revstack-modal/RevstackChangeSkuModal";

interface RevstackDashboardGridLayoutProps {
    moduleData: any;
    config: any;
}

/**
 * !Note[DND]: 
 * This component is used to render the dashboard level components not drilldown or trends components
 * This has no relation wrt to the grid layout in card, it lets you render the dashboard components in grids
 * WRT to view at a time we can render either a single component or 1xn (max 3, havent set limit for it) components in a row
 */

const RevstackDashboardGridLayout: React.FC<RevstackDashboardGridLayoutProps> = memo(({
    moduleData,
    config,
}) => {
    const dispatch = useDispatch();

    const revstackPopupData = useSelector(
        (state: { showRevstackPopup: { value: IRevstackPopupData } }) => state.showRevstackPopup.value
    );
    const revstackTrendsWidgetData = useSelector(
        (state: { revstackWidgetData: IRevstackWidgetData }) =>
            state.revstackWidgetData.drilldownTrends
    );
    const [modalConfig, setModalConfig] = useState<any>(null);

    // Process widgets in order and create rows for grid widgets while preserving order
    const processWidgetsInOrder = () => {
        const result: any[] = [];
        let currentGridRow: any[] = [];
        let currentRowSpan = 0;

        config.forEach((widgetConfig: any, idx: number) => {
            const layout = widgetConfig.config?.layout || { colSpan: 24 };

            if (layout.colSpan < 24) {
                // Grid widget - add to current row or start new row
                if (currentRowSpan + layout.colSpan > 24) {
                    // Current row is full, push it and start new row
                    if (currentGridRow.length > 0) {
                        result.push({
                            type: 'grid-row',
                            widgets: currentGridRow
                        });
                    }
                    currentGridRow = [widgetConfig];
                    currentRowSpan = layout.colSpan;
                } else {
                    // Add to current row
                    currentGridRow.push(widgetConfig);
                    currentRowSpan += layout.colSpan;
                }
            } else {
                // Full width widget - push any pending grid row first, then add this widget
                if (currentGridRow.length > 0) {
                    result.push({
                        type: 'grid-row',
                        widgets: currentGridRow
                    });
                    currentGridRow = [];
                    currentRowSpan = 0;
                }
                result.push({
                    type: 'full-width',
                    widget: widgetConfig
                });
            }
        });

        // Push any remaining grid row
        if (currentGridRow.length > 0) {
            result.push({
                type: 'grid-row',
                widgets: currentGridRow
            });
        }

        return result;
    };

    const processedWidgets = processWidgetsInOrder();

    const renderWidgetWithLayout = (widgetConfig: any, idx: number) => {
        const WidgetComponent = widgetConfig.widget;
        const layout = widgetConfig.config?.layout || {
            rowSpan: 1,
            colSpan: 24,
            className: "mb-3"
        };

        // If colSpan is less than 24, render as a grid item
        if (layout.colSpan < 24) {
            return (
                <Col
                    span={layout.colSpan}
                    key={`${widgetConfig.sectionId}-${idx}`}
                    className={layout.className}
                >
                    <WidgetComponent
                        widgetConfig={widgetConfig}
                        widgetData={moduleData}
                        title={widgetConfig.title}
                        isTrendsBtnRequired={widgetConfig?.isTrendsBtnRequired}
                        widgets={widgetConfig.widgets}
                        icon={widgetConfig.icon}
                    />
                </Col>
            );
        }

        // Full width widgets
        return (
            <div key={`${widgetConfig.sectionId}-${idx}`} className={layout.className}>
                <WidgetComponent
                    widgetConfig={widgetConfig}
                    widgetData={moduleData}
                    title={widgetConfig.title}
                    isTrendsBtnRequired={widgetConfig?.isTrendsBtnRequired}
                    widgets={widgetConfig.widgets}
                    modalConfig={{ modalConfig }}
                    icon={widgetConfig.icon}
                />
            </div>
        );
    };

    return (
        <Row className="w-100 py-3">
            <Col span={24}>
                {processedWidgets.map((item, index) => {
                    if (item.type === "full-width") {
                        return renderWidgetWithLayout(item.widget, index);
                    } else if (item.type === "grid-row") {
                        return (
                            <Row
                                key={`grid-row-${index}`}
                                gutter={[20, 20]}
                                className="mb-3"
                            >
                                {item.widgets.map(
                                    (widgetConfig: any, widgetIndex: number) =>
                                        renderWidgetWithLayout(
                                            widgetConfig,
                                            index
                                        )
                                )}
                            </Row>
                        );
                    }
                    return null;
                })}
            </Col>
            {revstackPopupData?.drilldown?.show && (
                <RevstackDrilldownDrawer
                    isOpen={revstackPopupData?.drilldown?.show}
                    activeSectionId={
                        revstackPopupData?.drilldown?.activeSectionId
                    }
                    config={revstackPopupData?.drilldown}
                    title={revstackPopupData?.drilldown?.title}
                    onClose={() => {
                        dispatch(
                            updateDrilldownState({
                                title: "",
                                show: false,
                                activeSectionId: null,
                                module: null
                            })
                        );
                        dispatch(resetDrilldownFilters());
                    }

                    }
                />
            )}

            {revstackPopupData?.trends?.show && (
                <RevstackDrilldownTrendsModal
                    isOpen={revstackPopupData?.trends?.show}
                    onClose={() => {
                        dispatch(
                            updateTrendsState({
                                show: false,
                                activeSectionId: null,
                                module: null,
                                defaultTab: null,
                                hideTrendsTabSection: false,
                                titlePills: []
                            })
                        );

                        dispatch(
                            resetTrendsFilters()
                        );
                    }

                    }
                    title="Trends Analysis"
                    titlePills={revstackPopupData?.trends?.titlePills || []}
                    widgetData={revstackTrendsWidgetData}
                    activeSectionId={
                        revstackPopupData?.trends?.activeSectionId ||
                        RevstackCASectionIds.SP
                    }
                    module={
                        revstackPopupData?.trends?.module ||
                        RevstackDashboardModules.CATEGORY_ANALYSIS
                    }
                    filterState={undefined}
                    filterDispatch={undefined}
                    drilldownFilters={undefined}
                    defaultTab={revstackPopupData?.trends?.defaultTab ||
                        CA_DD_MODULES.OVERVIEW}
                />
            )}

            {revstackPopupData?.changeSku?.show && (
                <RevstackChangeSkuModal
                    isOpen={revstackPopupData?.changeSku?.show}
                    onClose={() => {
                        dispatch(updateChangeSkuState({
                            show: false
                        }));
                    }}
                />
            )}
        </Row>
    );
});

export default RevstackDashboardGridLayout;
